/* eslint-disable no-unused-expressions */
/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import {
  Table as MuiTable,
  TableHead,
  TableRow,
  TableSortLabel,
  TableBody,
  Checkbox,
} from '@material-ui/core';
import chunk from 'lodash/chunk';
import TablePagination from './Pagination';
import Sorter from './Sorter';
import Filter from './Filter';
import StyledTableCell from './TableCell';
import TablePlaceHolder from './TablePlaceHolder';
import classes from './Table.module.scss';
import useSelectValue from 'hooks/useSelectValue';
import difference from 'lodash/difference';
import clsx from 'clsx';

const PAGINATION_DEFAULT = {
  rowsPerPageOptions: [10, 20, 50],
  pageSize: 10,
  total: 0,
  current: 1,
};
const Table = ({
  getRowKey,
  columns = [],
  dataSource = [],
  onRow,
  onChange,
  rowSelection,
  pagination = {
    ...PAGINATION_DEFAULT,
  },
  innerRef,
  draggablePlaceholder,
  placeholder,
  filtered,
  className,
  ...props
}) => {
  const settingPagination = useMemo(() => {
    return { ...PAGINATION_DEFAULT, ...pagination };
  }, [pagination]);
  const [selected, setSelected, isChecked, handleSelect] = useSelectValue([]);
  const [filter, setFilter] = React.useState(filtered || {});
  const [currentSort, setSorter] = React.useState({});
  const [currentPage, setCurrentPage] = React.useState(settingPagination.current);
  const [pageSize, setCurrentPageSize] = React.useState(settingPagination.pageSize);

  const handleChangePagination = (nextPage, nextPageSize) => {
    setSelected([]);
    rowSelection?.onSelectAll([], []);
    setCurrentPageSize(nextPageSize);
    setCurrentPage(nextPage);
    onChange &&
      onChange(
        {
          current: nextPage,
          pageSize: nextPageSize,
        },
        filter,
        currentSort,
        { action: 'pagination' }
      );
  };
  const handleChangeSort = (newSort) => {
    onChange &&
      onChange(
        {
          current: settingPagination.current,
          pageSize: settingPagination.pageSize,
        },
        filter,
        newSort,
        { action: 'sort' }
      );
  };
  const handleChangeFilter = React.useCallback(
    (newFilter) => {
      setFilter((prevState) => {
        return { ...prevState, ...newFilter };
      });

      onChange &&
        onChange(
          {
            current: settingPagination.current,
            pageSize: settingPagination.pageSize,
          },
          { ...filter, ...newFilter },
          currentSort,
          { action: 'filter' }
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );
  const data = useMemo(() => {
    if (!dataSource.length) return [];
    if (onChange) {
      return dataSource;
    }
    const sortBy = Object.keys(currentSort);
    let rawData = [...dataSource];
    if (sortBy.length) {
      const { orderBy, sorter } = currentSort[sortBy[0]];
      rawData.sort((a, b) => {
        if (sortBy[0]) {
          // object array sorting
          return orderBy === 'asc' ? a[sortBy[0]] - b[sortBy[0]] : b[sortBy[0]] - a[sortBy[0]];
        }
        return sorter(a, b) * (orderBy === 'asc' ? 1 : -1); // array sorting
      });
    }
    const chunked = chunk(rawData, pageSize);
    return chunked[currentPage - 1] || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, currentPage, pageSize, currentSort]);

  const dataSelectedChanged = useMemo(() => {
    const selectedKeys = data.map((record) => `${getRowKey(record)}`);
    if (!selected.length) return false;
    return difference(selectedKeys, selected).length >= dataSource.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  React.useEffect(() => {
    setSelected([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSelectedChanged]);

  React.useEffect(() => {
    setCurrentPage(settingPagination.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingPagination.current]);

  React.useEffect(() => {
    setCurrentPageSize(settingPagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingPagination.pageSize]);

  React.useEffect(() => {
    if (!rowSelection?.selection) return;
    setSelected(rowSelection.selection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const renderHeaderCell = ({
    title,
    sorter,
    filterDropdown,
    filterIcon,
    align,
    dataIndex,
    key,
  }) => {
    if (sorter) {
      return (
        <TableSortLabel
          hideSortIcon
          onClick={() => {
            if (currentSort[dataIndex]?.orderBy === 'desc') {
              setSorter({});
              handleChangeSort({});
              return;
            }

            const direction = currentSort[dataIndex]?.orderBy === 'asc' ? 'desc' : 'asc';
            setSorter({
              [dataIndex]: {
                orderBy: direction,
                sorter,
              },
            });
            handleChangeSort({
              [dataIndex]: direction,
            });
          }}
          active={Object.keys(sorter)[0] === dataIndex}
          direction={currentSort[dataIndex]?.orderBy}
          align={align || 'left'}>
          <span>{title}</span>
          <Sorter direction={currentSort[dataIndex]?.orderBy} />
        </TableSortLabel>
      );
    }

    if (filterDropdown) {
      return (
        <Filter
          value={filter[key || dataIndex] || []}
          dataIndex={key || dataIndex}
          handleChangeFilter={handleChangeFilter}
          filterIcon={filterIcon}
          filterDropdown={filterDropdown}
          title={title}></Filter>
      );
    }
    return <span>{title}</span>;
  };
  return (
    <>
      <MuiTable
        className={className}
        {...props}
        ref={(ref) => {
          innerRef && innerRef(ref);
        }}>
        <TableHead>
          <TableRow>
            {rowSelection?.onSelectAll ? (
              <StyledTableCell width={40}>
                <Checkbox
                  onClick={(event) => {
                    event.stopPropagation();
                    if (selected.length === data.length) {
                      setSelected([]);
                      rowSelection?.onSelectAll([], []);
                      return;
                    }
                    setSelected(data.map((record) => `${getRowKey(record)}`));
                    rowSelection?.onSelectAll(
                      data.map((record) => `${getRowKey(record)}`),
                      data
                    );
                  }}
                  indeterminate={selected.length > 0 && selected.length < data.length}
                  checked={selected.length > 0 && selected.length === data.length}></Checkbox>
              </StyledTableCell>
            ) : (
              undefined
            )}
            {columns.map(
              (
                {
                  title,
                  sorter,
                  align,
                  width,
                  dataIndex,
                  render,
                  filterIcon,
                  filterDropdown,
                  onCell,
                  key,
                  ...propsCell
                },
                index
              ) => (
                <StyledTableCell
                  key={`header-${dataIndex}-${index}`}
                  align={align || 'left'}
                  width={width}
                  {...propsCell}>
                  {renderHeaderCell({
                    title,
                    sorter,
                    align,
                    width,
                    dataIndex,
                    render,
                    filterIcon,
                    filterDropdown,
                    key,
                  })}
                </StyledTableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!data.length && <TablePlaceHolder placeholder={placeholder} />}
          {!!data.length &&
            data.map((record, index) => {
              const { className, renderRow, ...rowProps } = (onRow && onRow(record, index)) || {};
              return renderRow ? (
                renderRow({
                  item: record,
                  index,
                  getRowKey,
                  key: getRowKey ? getRowKey(record) : index,
                  isChecked,
                  className: clsx(
                    className,
                    !!isChecked(`${getRowKey(record)}`) ? classes.rowSelected : ''
                  ),
                  handleSelect,
                  rowSelection,
                  columns,
                  ...rowProps,
                })
              ) : (
                <TableRow
                  {...rowProps}
                  hover
                  className={clsx(
                    className,
                    classes.row,
                    !!isChecked(`${getRowKey(record)}`) ? classes.rowSelected : ''
                  )}
                  key={getRowKey ? getRowKey(record) : index}>
                  {
                    <>
                      {rowSelection ? (
                        <StyledTableCell width={40}>
                          <Checkbox
                            value={getRowKey(record)}
                            onChange={(event) => {
                              handleSelect(event.target.value);
                              rowSelection?.onSelect(record, event.target.value);
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            checked={isChecked(`${getRowKey(record)}`)}></Checkbox>
                        </StyledTableCell>
                      ) : (
                        undefined
                      )}
                      {columns.map(
                        (
                          {
                            render,
                            dataIndex,
                            align,
                            width,
                            sorter,
                            filterDropdown,
                            filterIcon,
                            onCell,
                            ...propsCell
                          },
                          columnIndex
                        ) => {
                          const cellProps = onCell ? onCell(record, columnIndex) : {};
                          return (
                            <StyledTableCell
                              align={align || 'left'}
                              width={width}
                              key={`${
                                getRowKey ? getRowKey(record) : index
                              }-${dataIndex}-${columnIndex}`}
                              {...propsCell}
                              {...cellProps}>
                              {render ? render(record[dataIndex], record) : record[dataIndex]}
                            </StyledTableCell>
                          );
                        }
                      )}
                    </>
                  }
                </TableRow>
              );
            })}
          {draggablePlaceholder}
        </TableBody>
      </MuiTable>
      {pagination && (
        <TablePagination
          onChange={handleChangePagination}
          className={classes.pagination}
          pageSizeOptions={settingPagination.rowsPerPageOptions}
          pageSize={pageSize}
          current={currentPage}
          total={settingPagination.total}
          siblingCount={1}
        />
      )}
    </>
  );
};

export default React.memo(Table);
